import React, { useState } from "react";
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    SelectChangeEvent
} from "@mui/material";
import { newSponsorInfo, SponsorListItem, TierListItem } from "../../data/sponsorInfo";
import "../admin/Admin.css";
import { addSponsor } from "../../services/SponsorService";
import {PartnerListItem} from "../../data/partnerData";

interface AddSponsorModalProps {
    isOpen: boolean;
    onClose: () => void;
    eventId: string;
    setSnackbarMessage: (message: string) => void;
    setSnackbarOpen: (open: boolean) => void;
    refreshSponsors: () => Promise<void>;
    partners: PartnerListItem[];
    tiers: TierListItem[];
}

const modalStyle = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 800,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
};

const AddSponsorModal: React.FC<AddSponsorModalProps> = ({ isOpen, onClose, setSnackbarMessage, setSnackbarOpen, refreshSponsors, eventId, partners, tiers }) => {
    const [sponsor, setSponsor] = useState<SponsorListItem>(newSponsorInfo());
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    sponsor.EventId = eventId;

    const validateFields = () => {
        let newErrors: { [key: string]: string } = {};
        let isValid = true;

        if (!sponsor.Name.trim()) {
            newErrors.Name = "Sponsor name is required.";
            isValid = false;
        }
        if (!sponsor.Link.trim() || !/^https?:\/\/.+\..+$/.test(sponsor.Link)) {
            newErrors.Link = "Valid link is required.";
            isValid = false;
        }
        if (!sponsor.LogoSmall.trim() || !/^https?:\/\/.+\..+$/.test(sponsor.LogoSmall)) {
            newErrors.LogoSmall = "Valid small logo link is required.";
            isValid = false;
        }
        if (!sponsor.PartnerId.trim()) {
            newErrors.PartnerId = "Partner selection is required.";
            isValid = false;
        }
        if (!sponsor.SponsorshipTierId.trim()) {
            newErrors.SponsorshipTierId = "Sponsor tier selection is required.";
            isValid = false;
        }
        if (!sponsor.CommitmentDate.trim()) {
            newErrors.CommitmentDate = "Commitment date is required.";
            isValid = false;
        }
        if (sponsor.DonationAmount <= 0) {
            newErrors.DonationAmount = "Donation amount must be greater than 0.";
            isValid = false;
        }
        if (sponsor.SortOrder < 0) {
            newErrors.SortOrder = "Sort order cannot be negative.";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleChange = (e: SelectChangeEvent<string> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setSponsor((prevSponsor) => ({
            ...prevSponsor,
            [name]: value,
        }));
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: "",
        }));
    };

    const handleSubmit = async () => {
        if (!validateFields()) return;
        setLoading(true);
        setErrorMessage(null);
        try {
            await addSponsor(sponsor);
            setSnackbarMessage("Sponsor added successfully");
            setSnackbarOpen(true);
            await refreshSponsors();

            setSponsor(newSponsorInfo()); //resets form

            onClose();
        } catch (error) {
            setErrorMessage("Failed to add sponsor. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={isOpen} onClose={onClose} aria-labelledby="add-sponsor-modal">
            <Box sx={modalStyle}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>Add A New Sponsor</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <TextField fullWidth label="Sponsor Name" name="Name" value={sponsor.Name} onChange={handleChange} margin="normal" error={!!errors.Name} helperText={errors.Name} />
                        <TextField fullWidth label="Link" name="Link" value={sponsor.Link} onChange={handleChange} margin="normal" error={!!errors.Link} helperText={errors.Link} />
                        <TextField fullWidth label="Logo Small" name="LogoSmall" value={sponsor.LogoSmall} onChange={handleChange} margin="normal" error={!!errors.LogoSmall} helperText={errors.LogoSmall} />
                        <TextField fullWidth label="Logo Medium" name="LogoMedium" value={sponsor.LogoMedium} onChange={handleChange} margin="normal" />
                        <TextField fullWidth label="Logo Large" name="LogoLarge" value={sponsor.LogoLarge} onChange={handleChange} margin="normal" />
                        <TextField fullWidth label="Logo Banner" name="LogoBanner" value={sponsor.LogoBanner} onChange={handleChange} margin="normal" />
                        <TextField fullWidth label="Sort Order" name="SortOrder" type="number" value={sponsor.SortOrder} onChange={handleChange} margin="normal" error={!!errors.SortOrder} helperText={errors.SortOrder} />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth margin="normal" error={!!errors.PartnerId}>
                            <InputLabel id="partner-label">Partner</InputLabel>
                            <Select labelId="partner-label" name="PartnerId" value={sponsor.PartnerId} onChange={handleChange} label="Partner">
                                <MenuItem value="" disabled>Select a Partner</MenuItem>
                                {partners?.map((partner) => (
                                    <MenuItem key={partner.PartnerId} value={partner.PartnerId}>{partner.Name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth margin="normal" error={!!errors.SponsorshipTierId}>
                            <InputLabel id="tier-label">Sponsor Tier</InputLabel>
                            <Select labelId="tier-label" name="SponsorshipTierId" value={sponsor.SponsorshipTierId} onChange={handleChange} label="Sponsor Tier">
                                <MenuItem value="" disabled>Select a Tier</MenuItem>
                                {tiers?.map((tier) => (
                                    <MenuItem key={tier.SponsorshipTierId} value={tier.SponsorshipTierId}>{tier.Name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField fullWidth label="Commitment Date" name="CommitmentDate" type="date" value={sponsor.CommitmentDate} onChange={handleChange} InputLabelProps={{ shrink: true }} margin="normal" error={!!errors.CommitmentDate} helperText={errors.CommitmentDate} />
                        <TextField fullWidth label="Donation Amount" name="DonationAmount" type="number" value={sponsor.DonationAmount} onChange={handleChange} margin="normal" error={!!errors.DonationAmount} helperText={errors.DonationAmount} />
                        <TextField fullWidth label="Notes" name="Notes" multiline rows={8} value={sponsor.Notes} onChange={handleChange} margin="normal" />
                    </Grid>
                </Grid>
                {errorMessage && <Typography color="error" sx={{ mt: 2 }}>{errorMessage}</Typography>}
                <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
                    <Button onClick={onClose} variant="outlined">Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" className="editOrAdd" disabled={loading}>{loading ? "Submitting..." : "Submit"}</Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddSponsorModal;